"use client";

import { Format } from '../../lib/formats';
import { CodeBlockLine, SupernovaCodeBlock } from '@/components/ui';

interface PandasViewSortFilterProps {
  format: Format;
}

export function PandasViewSortFilter({ format }: PandasViewSortFilterProps) {
  return (
    <div className="prose prose-gray dark:prose-invert">
      <p>First, we need to install pandas</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install pandas"}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`Then we can load the ${format} file into a dataframe.`}</p>
      <PandasFromStatement format={format} />
      <p>{`We can view the first few rows of the dataframe using the head method.`}</p>
      <PandasHeadPrintStatement n={5} />
      <p>The n parameter controls how many rows are returned. Increase it to show more rows.</p>
      <p>{`We can view the last few rows of the dataframe using the tail method.`}</p>
      <PandasTailPrintStatement n={5} />
      <p>{'We can sort the dataframe using the sort_values method.'}</p>
      <PandasSortByColumnValuesStatement col="column_name" ascending={true} />
      <p>{`Just replace 'column_name' with the name of the column you want to sort by. The 'ascending' parameter controls whether the values will be sorted in 'ascending' or 'descending' order.`}</p>
      <p>{`We can filter the dataframe using comparison operators. The following statement will filter a dataframe to rows where the value of the 'column_name' column is greater than 5.`}</p>
      <PandasFilterStatement col="column_name" op=">" val="5" />
    </div>
  );
}

interface DuckDBViewSortFilterProps {
  format: Format;
}

export function DuckDBViewSortFilter({ format }: DuckDBViewSortFilterProps) {
  return (
    <div className="prose prose-gray dark:prose-invert">
      <p>First, we need to install duckdb for Python</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install duckdb"}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`The following duckdb query will create a view from the input ${format} file.`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`duckdb.sql("""SELECT * from path/to/file.${format}""")`}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`Sometimes we have large file and it's impractical to read the whole file. We can read the first 5 rows using the following.`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`duckdb.sql("""SELECT * from path/to/file.${format} limit 5""")`}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`We can sort rows using the ORDER BY clause and a SQL comparison operator.`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`duckdb.sql("""SELECT * from path/to/file.${format} order by 'column_name' ASC limit 5""")`}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`Just change 'column_name' for the column you want to sort by. Use ASC to sort ascending or DESC to sort descending.`}</p>
      <p>{`We can also filter using SQL comparison operators and the WHERE clause.`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`duckdb.sql("""SELECT * from path/to/file.${format} where 'column_name' > 5 ASC limit 5""")`}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`You can change the 'column_name' to change the column you want to filter by. The operator (>) and value (5) control how the filtering is applied to 'column_name'.`}</p>
    </div>
  );
}

interface PandasFromStatementProps {
  format: Format;
}

function PandasFromStatement({ format }: PandasFromStatementProps) {
  if (format === Format.CSV) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>{"df = pd.read_csv('path/to/file.csv')"}</CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (format === Format.TSV) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df = pd.read_csv('path/to/file.tsv', sep='\\t')"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (format === Format.PARQUET) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df = pd.read_parquet('path/to/file.parquet')"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (format === Format.XLSX) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df = pd.read_excel('path/to/file.xlsx')"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (format === Format.JSON) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df = pd.read_json('path/to/file.json')"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else {
    console.error({ format });
    throw new Error("Missing From");
  }
}

interface PandasHeadPrintStatementProps {
  n: number;
}

function PandasHeadPrintStatement({ n }: PandasHeadPrintStatementProps) {
  return (
    <SupernovaCodeBlock>
      <CodeBlockLine>{`print(df.head(n=${n}))`}</CodeBlockLine>
    </SupernovaCodeBlock>
  );
}

interface PandasTailPrintStatementProps {
  n: number;
}

function PandasTailPrintStatement({ n }: PandasTailPrintStatementProps) {
  return (
    <SupernovaCodeBlock>
      <CodeBlockLine>{`print(df.tail(n=${n}))`}</CodeBlockLine>
    </SupernovaCodeBlock>
  );
}

interface PandasSortByColumnValuesStatementProps {
  col: string;
  ascending: boolean;
}

function PandasSortByColumnValuesStatement({ col, ascending }: PandasSortByColumnValuesStatementProps) {
  return (
    <SupernovaCodeBlock>
      <CodeBlockLine>{`df = df.sort_values('${col}', ascending=${ascending})`}</CodeBlockLine>
    </SupernovaCodeBlock>
  );
}

interface PandasFilterStatementProps {
  col: string;
  op: string;
  val: string;
}

function PandasFilterStatement({ col, op, val }: PandasFilterStatementProps) {
  return (
    <SupernovaCodeBlock>
      <CodeBlockLine>{`df = df[df['${col}'] ${op} ${val}]`}</CodeBlockLine>
    </SupernovaCodeBlock>
  );
}

interface HowToViewSortProps {
  format: Format;
  className?: string;
}

export function HowToViewSort({ format, className }: HowToViewSortProps) {
  return (
    <div className={`my-8 ${className}`}>
      <div className="prose prose-gray dark:prose-invert max-w-none">
        <h2 className="text-2xl font-bold">{`How to view and sort ${format} files in Python`}</h2>
        <p>
          We can view and sort {format} files in Python using Pandas or DuckDB
        </p>
        <h3>{`How to view and sort ${format} files using Pandas`}</h3>
        <PandasViewSortFilter format={format} />
        <h3>{`How to view and sort ${format} files using DuckDB`}</h3>
        <DuckDBViewSortFilter format={format} />
      </div>
    </div>
  );
} 